import { useCallback, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { extractErrorMessage } from "../../api/client";
import { patchSearchEnv, SearchEnv } from "../../api/searchEnvs";
import { WorkflowDetail } from "../../api/workflows";
import { OutputRetentionPanel } from "./OutputRetentionPanel";

// Consolidated retention controls for the workflow's dataset AND
// (when one exists) its active search environment. Lives between
// the Customer Search section and Cost Summary on /exports/<id>;
// before this it was scattered — dataset retention inside "Result
// Data", env retention inside the search env card.
//
// Single source of truth for two-step retention thinking: "I'm
// keeping the dataset around long enough for the search env to
// stay live, and the env around long enough for the customer to
// finish their analysis."

interface Props {
  wf: WorkflowDetail;
  onWorkflowUpdated: (wf: WorkflowDetail) => void;
  // Active search env, if any. We accept the array from the parent
  // and pick the first non-terminal one ourselves so the panel
  // stays self-contained — same convention SearchEnvPanel uses.
  envs: SearchEnv[] | null;
  // Called after a successful env-TTL extend so the parent's poll
  // picks up the new expires_at on the next tick. The PATCH may
  // also cascade the workflow's output_expires_at; in that case
  // ``onWorkflowUpdated`` is invoked too so the dataset chip
  // refreshes alongside the env chip.
  onEnvExtended: () => void;
}

function _extendLabel(hours: number): string {
  if (hours < 24) return `${hours}h`;
  const days = hours / 24;
  return Number.isInteger(days) ? `${days}d` : `${days.toFixed(1)}d`;
}

function formatTtl(ms: number): string {
  if (ms <= 0) return "expired";
  const totalSec = Math.floor(ms / 1000);
  const days = Math.floor(totalSec / 86400);
  const hours = Math.floor((totalSec % 86400) / 3600);
  const mins = Math.floor((totalSec % 3600) / 60);
  if (days > 0) return `${days}d ${hours}h`;
  if (hours > 0) return `${hours}h ${mins}m`;
  return `${mins}m`;
}

function ttlChipColor(ms: number): "default" | "warning" | "error" {
  if (ms < 6 * 3600 * 1000) return "error";
  if (ms < 48 * 3600 * 1000) return "warning";
  return "default";
}

const ENV_TERMINAL = new Set(["deleted", "deleting"]);

export function DataRetentionPanel({
  wf,
  onWorkflowUpdated,
  envs,
  onEnvExtended,
}: Props) {
  const liveEnv =
    (envs || []).find((e) => !ENV_TERMINAL.has(e.status)) || null;

  return (
    <Stack spacing={2}>
      <OutputRetentionPanel wf={wf} onUpdated={onWorkflowUpdated} />
      {liveEnv && <SearchEnvRetentionRow env={liveEnv} onExtended={onEnvExtended} />}
    </Stack>
  );
}

function SearchEnvRetentionRow({
  env,
  onExtended,
}: {
  env: SearchEnv;
  onExtended: () => void;
}) {
  const [extending, setExtending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Cascade-message banner: when extending the env's TTL pushes past
  // the dataset's expires_at, the PATCH response includes
  // ``dataset_extended_to``. We surface that so the cascade isn't
  // silent — the user sees both TTLs move on a single click. The
  // dataset chip refresh follows via the parent's workflow poll.
  const [cascadeNotice, setCascadeNotice] = useState<{ to: number } | null>(
    null,
  );

  const handleExtend = useCallback(
    async (hours: number) => {
      setExtending(true);
      setError(null);
      try {
        const resp = await patchSearchEnv(env.id, { ttl_hours: hours });
        onExtended();
        if (resp.dataset_extended_to != null) {
          setCascadeNotice({ to: resp.dataset_extended_to });
        }
      } catch (err) {
        setError(extractErrorMessage(err));
      } finally {
        setExtending(false);
      }
    },
    [env.id, onExtended],
  );

  const expiresMs = env.expires_at - Date.now();
  const color = ttlChipColor(expiresMs);

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack spacing={1}>
        <Typography variant="overline" color="text.secondary">
          Search environment retention
        </Typography>
        {error && <Alert severity="error">{error}</Alert>}
        {cascadeNoticeBanner(cascadeNotice)}
        <Box>
          <Stack
            direction="row"
            spacing={1.5}
            alignItems="center"
            flexWrap="wrap"
          >
            <Typography variant="body2">
              {env.id} (
              <Box component="span" sx={{ color: "text.secondary" }}>
                {env.status}
              </Box>
              )
            </Typography>
            <Chip
              size="small"
              color={color}
              variant={color === "default" ? "outlined" : "filled"}
              label={formatTtl(expiresMs)}
            />
            <Typography variant="caption" color="text.secondary">
              ({new Date(env.expires_at).toLocaleString()})
            </Typography>
          </Stack>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
          <Typography variant="body2" color="text.secondary">
            Extend by:
          </Typography>
          {[1, 4, 24, 24 * 7, 24 * 30, 24 * 90].map((h) => (
            <Button
              key={h}
              size="small"
              variant="text"
              disabled={extending}
              onClick={() => handleExtend(h)}
            >
              {_extendLabel(h)}
            </Button>
          ))}
        </Stack>
        <Typography variant="caption" color="text.secondary">
          Extending past the dataset's expiry will push the dataset
          forward to match — a customer search env can't outlive
          its source data.
        </Typography>
      </Stack>
    </Paper>
  );
}

function cascadeNoticeBanner(notice: { to: number } | null) {
  if (!notice) return null;
  return (
    <Alert severity="info">
      Dataset retention also extended to{" "}
      {new Date(notice.to).toLocaleString()} so the search env stays
      backed by data through its full TTL.
    </Alert>
  );
}
